import { useEffect, useRef, useState } from 'react'
import { ChevronDown, Landmark, Wallet } from 'lucide-react'
import { CustodyLogo } from './custody-logo'
import { KRAKEN_LOGO } from '@/lib/custody'
import { CUSTODY_LABELS, useCustody, type Custody, type ExchangeId } from '@/lib/pool'

export type PlatformOption = { id: string; label: string }

/** the exchanges in the order the chips show them (Sonar, once first, retired Nov 3 2026) */
const PLATFORM_ORDER: ExchangeId[] = ['mexc', 'binanceus', 'coinbase']

/** The exchanges this deployment offers for SOL, USDC and USDT. */
export function usePlatformOptions(): PlatformOption[] {
  const { custodies } = useCustody()
  return PLATFORM_ORDER.filter((id) => custodies[id]).map((id) => ({ id, label: CUSTODY_LABELS[id] }))
}

/** a bank saved on Kraken, as a place id: `bank:<the name Kraken knows it under>` (Nov 2 2026) */
export const bankPlace = (name: string) => `bank:${name}`
const bankName = (id: string) => (id.startsWith('bank:') ? id.slice('bank:'.length) : null)

/** the banks we have a mark for, by how their saved name reads; John supplied the AMEX logo (Nov 2 2026) */
const BANK_LOGOS: { match: RegExp; src: string }[] = [{ match: /\bamex\b|american express/i, src: '/brand/banks/amex.png' }]
export const bankLogo = (name: string): string | null => BANK_LOGOS.find((bank) => bank.match.test(name))?.src ?? null

/** a bank's mark: its logo when we have one, a bank glyph otherwise */
export function BankMark({ name, size = 'size-5' }: { name: string; size?: string }) {
  const src = bankLogo(name)
  if (src) return <img src={src} alt="" className={`${size} shrink-0 rounded-md object-contain`} />
  return (
    <span className={`flex ${size} shrink-0 items-center justify-center rounded-full bg-surface-2 text-muted`}>
      <Landmark className="size-[62%]" aria-hidden="true" />
    </span>
  )
}

/** a place's label: a platform, Kraken, a bank, the account's own vault, or any wallet address */
export function platformLabel(id: string): string {
  const bank = bankName(id)
  if (bank !== null) return bank
  if (id === 'kraken') return 'Kraken'
  if (id === 'vault') return 'Vault'
  if (id === 'wallet') return 'External wallet address'
  return CUSTODY_LABELS[id as Custody] ?? id
}

/** the mark of a place: a platform's logo, Kraken's, the Dispence tile for the vault, a wallet glyph for any address */
export function PlatformMark({ id, size = 'size-5' }: { id: string; size?: string }) {
  const bank = bankName(id)
  if (bank !== null) return <BankMark name={bank} size={size} />
  if (id === 'wallet') return <Wallet className={`${size} shrink-0 text-muted`} aria-hidden="true" />
  if (id === 'kraken') return <img src={KRAKEN_LOGO} alt="" className={`${size} shrink-0 object-contain`} />
  if (id === 'vault') return <img src="/brand/logo-tile.png" alt="" className={`${size} shrink-0 rounded-md`} />
  return <CustodyLogo custody={id as Custody} size={size} />
}

/**
 * Every platform's logo in one bordered group, the chosen one lit the way a
 * pressed chip is and the rest dimmed; a click picks (John, Nov 2 2026:
 * "show all CEX options on swaps so i can click to select highlight the one
 * rather than cycle", "a 3-in-1 grouping"). A value outside the options
 * is still shown, lit, at the end.
 * While a pick is being remembered the group looks the same and ignores
 * clicks, so nothing shifts (the PlaceMenu rule).
 */
export function PlatformGroup({ value, options, onChange, disabled = false, className = '' }: { value: string; options: PlatformOption[]; onChange: (id: string) => void; disabled?: boolean; className?: string }) {
  const shown = options.some((option) => option.id === value) ? options : [...options, { id: value, label: platformLabel(value) }]
  return (
    <span role="radiogroup" aria-label="Platform" className={`inline-flex h-10 items-center gap-0.5 rounded-lg border border-line p-px ${className}`}>
      {shown.map((option) => {
        const active = option.id === value
        return (
          <button
            key={option.id}
            type="button"
            role="radio"
            aria-checked={active}
            aria-disabled={disabled || undefined}
            aria-label={option.label}
            title={option.label}
            className={`flex size-9 items-center justify-center rounded-md border transition-[border-color,background-color,opacity] duration-150 ${active ? 'border-accent/70 bg-surface-2' : 'border-transparent opacity-50 hover:opacity-100'}`}
            onClick={() => {
              if (!disabled && !active) onChange(option.id)
            }}
          >
            <PlatformMark id={option.id} size="size-6" />
          </button>
        )
      })}
    </span>
  )
}

/** a place named beside an address in a review or summary row */
export function PlaceChip({ id }: { id: string }) {
  // neutral: green is Dispence's colour and each place has its own, in its logo (John, Nov 1 2026: "making them green is ugly")
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-surface-3 py-0.5 pl-1.5 pr-2 text-xs font-medium text-text">
      <PlatformMark id={id} size="size-3.5" />
      {platformLabel(id)}
    </span>
  )
}

export type PlaceOption = PlatformOption & {
  /** a figure beside the name in the list, e.g. what the place holds of the token */
  detail?: string
}

/**
 * A place picked from a menu (John, Nov 1 2026: "a dropdown of from places
 * that shows the token holdings it has", "To ... a dropdown and pick the
 * destination, rather than a chip cycler"). The button shows the pick with
 * its logo; the list shows every option with its logo, name and figure.
 */
export function PlaceMenu({ value, options, onChange, disabled = false, align = 'left', opens = 'down', className = '' }: { value: string; options: PlaceOption[]; onChange: (id: string) => void; disabled?: boolean; align?: 'left' | 'right'; /** up, for a menu near the bottom of a dialog, where a list opening down is cut off */ opens?: 'down' | 'up'; className?: string }) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!open) return
    const onPointerDown = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) setOpen(false)
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])
  const current = options.find((option) => option.id === value) ?? { id: value, label: platformLabel(value) }
  const hasMenu = options.length > 1
  const canPick = hasMenu && !disabled
  // the list is a grid: the option buttons are inline-flex, and nowrap on the list laid them out sideways (Nov 1 2026)
  // disabled only stops the list opening: the button looks the same and keeps its chevron, so nothing shifts while a
  // pick is being remembered (John, Nov 1 2026: "size flashing narrow/wide ... the in-between is not smooth")
  return (
    <div ref={ref} className={`relative ${className}`}>
      <button
        type="button"
        className={`control flex h-10 w-full items-center gap-2 px-3 text-sm ${hasMenu ? 'hover:text-text' : 'cursor-default'}`}
        aria-disabled={disabled || undefined}
        onClick={() => {
          if (canPick) setOpen((value) => !value)
        }}
        aria-haspopup={hasMenu ? 'menu' : undefined}
        aria-expanded={hasMenu ? open : undefined}
      >
        <PlatformMark id={current.id} size="size-4" />
        {/* every option's name sits in the same cell, unseen, so the button keeps the width of the widest (John, Nov 1 2026) */}
        <span className="grid min-w-0 flex-1 text-left">
          {options.map((option) => (
            <span key={option.id} className="invisible col-start-1 row-start-1 whitespace-nowrap" aria-hidden="true">{option.label}</span>
          ))}
          <span className="col-start-1 row-start-1 truncate">{current.label}</span>
        </span>
        {hasMenu ? <ChevronDown className="size-4 shrink-0 text-muted" aria-hidden="true" /> : null}
      </button>
      {open ? (
        <div role="menu" className={`card absolute z-50 grid min-w-full gap-0.5 p-1.5 shadow-lg shadow-black/40 ${opens === 'up' ? 'bottom-full mb-2' : 'top-full mt-2'} ${align === 'right' ? 'right-0' : 'left-0'}`}>
          {options.map((option) => (
            <button
              key={option.id}
              type="button"
              role="menuitemradio"
              aria-checked={option.id === value}
              className="btn btn-ghost w-full !justify-start gap-2.5 whitespace-nowrap !px-2 !py-2 text-sm"
              onClick={() => {
                setOpen(false)
                if (option.id !== value) onChange(option.id)
              }}
            >
              <PlatformMark id={option.id} size="size-4" />
              <span className="flex-1 text-left">{option.label}</span>
              {option.detail ? <span className="pl-4 text-xs tabular-nums text-muted">{option.detail}</span> : null}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  )
}
